import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { ShoppingCart, Trash2, Plus, Minus, Loader2, Truck, Store, ArrowLeft, CreditCard, Package, Info, Weight } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CartRecommendations } from '../components/RelatedProducts';

export default function CartPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cart, loading, updateCartItem, removeFromCart } = useCart();
    
    const [deliveryType, setDeliveryType] = useState('pickup');
    const [updating, setUpdating] = useState(null);

    // Get shipping cost from cart data
    const shippingCost = cart?.shipping?.price || 0;
    const totalWeight = cart?.total_weight || 0;

    const handleQuantityChange = async (productId, newQuantity) => {
        if (newQuantity < 1) {
            handleRemove(productId);
            return;
        }
        setUpdating(productId);
        try {
            await updateCartItem(productId, newQuantity);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUpdating(null);
        }
    };

    const handleRemove = async (productId) => {
        setUpdating(productId);
        try {
            await removeFromCart(productId);
            toast.success(t('cart.removed'));
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUpdating(null);
        }
    };

    const deliveryFee = deliveryType === 'delivery' ? shippingCost : 0;
    const total = (cart?.total_with_discount || cart?.total || 0) + deliveryFee;

    const handleCheckout = () => {
        if (!user) {
            toast.error(t('auth.fillAllFields'));
            navigate('/auth');
            return;
        }
        navigate('/checkout', { state: { deliveryType } });
    };

    if (!user) {
        return (
            <div className="min-h-screen pt-20 pb-12" data-testid="cart-page">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
                    <ShoppingCart className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                    <h2 className="font-heading text-2xl font-bold mb-2">{t('cart.loginToSeeCart')}</h2>
                    <p className="text-muted-foreground mb-6">
                        {t('cart.loginToSeeCartDesc')}
                    </p>
                    <Link to="/auth">
                        <Button className="rounded-full">{t('auth.loginButton')}</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="cart-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => navigate('/shop')}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('cart.continueShopping')}
                    </Button>
                </div>

                <div className="flex items-center gap-3 mb-8">
                    <ShoppingCart className="h-8 w-8 text-primary" />
                    <h1 className="font-heading text-3xl font-bold">{t('cart.title')}</h1>
                    {cart.items.length > 0 && (
                        <span className="text-muted-foreground">({cart.items.length} {t('common.items')})</span>
                    )}
                </div>

                {cart.items.length === 0 ? (
                    <Card className="glass rounded-3xl">
                        <CardContent className="py-16 text-center">
                            <ShoppingCart className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                            <h2 className="font-heading text-xl font-semibold mb-2">{t('cart.empty')}</h2>
                            <p className="text-muted-foreground mb-6">
                                {t('cart.emptyDesc')}
                            </p>
                            <Link to="/shop">
                                <Button className="rounded-full">{t('common.viewShop')}</Button>
                            </Link>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Cart items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cart.items.map(item => (
                                <Card key={item.product_id} className="glass rounded-2xl" data-testid={`cart-item-${item.product_id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex gap-4">
                                            <div className="h-24 w-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                                                <img
                                                    src={item.image_url}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <Link to={`/shop/${item.product_id}`}>
                                                    <h3 className="font-heading font-semibold hover:text-primary transition-colors">
                                                        {item.name}
                                                    </h3>
                                                </Link>
                                                <p className="text-sm text-muted-foreground">{item.brand}</p>
                                                <p className="font-mono text-primary font-semibold mt-1">
                                                    {formatPrice(item.price)}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="rounded-full h-8 w-8 text-muted-foreground hover:text-destructive"
                                                    onClick={() => handleRemove(item.product_id)}
                                                    disabled={updating === item.product_id}
                                                    data-testid={`remove-${item.product_id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="secondary"
                                                        size="icon"
                                                        className="rounded-full h-8 w-8"
                                                        onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                                        disabled={updating === item.product_id}
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </Button>
                                                    <span className="w-8 text-center font-mono font-semibold">
                                                        {updating === item.product_id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                                                        ) : (
                                                            item.quantity
                                                        )}
                                                    </span>
                                                    <Button
                                                        variant="secondary"
                                                        size="icon"
                                                        className="rounded-full h-8 w-8"
                                                        onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                                        disabled={updating === item.product_id}
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                                <span className="font-semibold">
                                                    {formatPrice(item.item_total)}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Order summary */}
                        <div className="space-y-4">
                            {/* Delivery options */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t('cart.deliveryMethod')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <RadioGroup
                                        value={deliveryType}
                                        onValueChange={setDeliveryType}
                                        className="space-y-3"
                                    >
                                        <div className={`flex items-center space-x-3 p-3 rounded-xl border transition-colors ${
                                            deliveryType === 'pickup' ? 'border-primary bg-primary/5' : 'border-border'
                                        }`}>
                                            <RadioGroupItem value="pickup" id="pickup" />
                                            <Label htmlFor="pickup" className="flex-1 cursor-pointer">
                                                <div className="flex items-center gap-2">
                                                    <Store className="h-4 w-4" />
                                                    <span className="font-medium">{t('cart.storePickup')}</span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{t('cart.storePickupDesc')}</p>
                                            </Label>
                                            <span className="font-semibold text-emerald-500">{t('common.free')}</span>
                                        </div>
                                        <div className={`flex items-center space-x-3 p-3 rounded-xl border transition-colors ${
                                            deliveryType === 'delivery' ? 'border-primary bg-primary/5' : 'border-border'
                                        }`}>
                                            <RadioGroupItem value="delivery" id="delivery" />
                                            <Label htmlFor="delivery" className="flex-1 cursor-pointer">
                                                <div className="flex items-center gap-2">
                                                    <Truck className="h-4 w-4" />
                                                    <span className="font-medium">Livraison Chronopost</span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">Livraison express 24-48h en France</p>
                                            </Label>
                                            <span className="font-semibold">{formatPrice(shippingCost)}</span>
                                        </div>
                                    </RadioGroup>

                                    {/* Chronopost info */}
                                    {deliveryType === 'delivery' && (
                                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                                            <div className="flex items-start gap-2">
                                                <Package className="h-4 w-4 text-blue-600 mt-0.5" />
                                                <div className="text-sm">
                                                    <p className="font-medium text-blue-700 dark:text-blue-300">Expédition via Chronopost</p>
                                                    <ul className="text-blue-600 dark:text-blue-400 text-xs mt-1 space-y-0.5">
                                                        <li>• Poids total : <strong>{totalWeight} kg</strong></li>
                                                        <li>• Livraison 24-48h (jours ouvrés)</li>
                                                        <li>• Suivi en temps réel</li>
                                                        <li>• France métropolitaine uniquement</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Summary */}
                            <Card className="glass rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t('cart.summary')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('cart.subtotal')}</span>
                                        <span className="font-mono">{formatPrice(cart.total)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('cart.delivery')}</span>
                                        <span className={`font-mono ${deliveryFee === 0 ? 'text-emerald-500' : ''}`}>
                                            {deliveryFee === 0 ? t('common.free') : formatPrice(deliveryFee)}
                                        </span>
                                    </div>
                                    <div className="border-t border-border pt-3 flex justify-between">
                                        <span className="font-heading font-semibold">{t('cart.total')}</span>
                                        <span className="font-heading font-bold text-xl text-primary" data-testid="cart-total">
                                            {formatPrice(total)}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Checkout button */}
                            <Button
                                size="lg"
                                className="w-full rounded-full h-14 font-semibold"
                                onClick={handleCheckout}
                                data-testid="checkout-btn"
                            >
                                <CreditCard className="h-5 w-5 mr-2" />
                                {t('cart.checkout')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Cart Recommendations */}
                {cart.items.length > 0 && <CartRecommendations />}
            </div>
        </div>
    );
}
